import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import {
	catchError,
	delay,
	retry,
	retryWhen,
	tap,
	mergeMap,
	first,
	switchMap,
	Observable,
	throwError,
	timer,
	iif,
	of,
	EMPTY,
} from 'rxjs';
import { RoutesManager } from '../shared/models/routes.interfaces';
import { LoggerService } from '../logger/logger.service';
import { AuthService } from '../auth/auth.service';
import { environment } from 'src/environments/environment';
import { EXCLUDED_HTTP_CALLS } from './excluded-http-calls';
import { SKIP_GLOBAL_ERROR_SNACKBAR } from './http-context-tokens';
import { MineSnackbarType } from '../shared/mine-snackbar/mine-snackbar-type';
import { MineSnackbarService } from '../shared/mine-snackbar/mine-snackbar.service';
import { ContentPipe } from '../services/content/content.pipe';
import { ProfileQuery } from '../profile/state/profile.query';
import { AuthProviders } from './models/auth/auth-providers';
import { LocalStorageHelper } from '../services/localStorage-helper';
import { HotjarService } from '../analytics/hotjar.service';
import { DialogsManagerService } from '../services/dialogs-manager.service';
import { SessionExpiredDialogComponent } from '../auth/session-expired-dialog/session-expired-dialog.component';
import { DialogRef } from '@ngneat/dialog';

const HTTP_TOO_MANY_REQUESTS = 429;
// The Cloud Armor WAF (`alb-gke-policy`, IP+SNI) returns 429 above 800 req/min and bans for 10 min
// above 500 req/min. Transparently retry throttled calls (honoring `Retry-After`) before surfacing
// them, so a transient burst self-heals instead of failing the request outright.
const MAX_RATE_LIMIT_RETRIES = 3;
const RATE_LIMIT_MAX_BACKOFF_MS = 8_000;

@Injectable({
	providedIn: 'root',
})
export class HttpErrorsInterceptor implements HttpInterceptor {
	private readonly loggerName: string = 'HttpErrorsInterceptor';

	private sessionExpiredDialogRef: DialogRef<SessionExpiredDialogComponent>;

	private sessionExpired = false;

	constructor(
		private logger: LoggerService,
		private authService: AuthService,
		private hotjar: HotjarService,
		private router: Router,
		private snackbarService: MineSnackbarService,
		private contentPipe: ContentPipe,
		private profileQuery: ProfileQuery,
		private dialogsManagerService: DialogsManagerService,
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		return next.handle(req).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error.status === HTTP_TOO_MANY_REQUESTS) {
					return this.handleRateLimitError(error, req, next);
				} else if (error.status >= 400 && error.status < 500) {
					return this.handleClientErrors(error, req);
				} else if (error.status >= 500 || error.status == 0) {
					return this.handleServerErrors(error, req, next);
				} else {
					this.handleErrorLogging(`General HTTP error. error code: ${error.status}, error message: ${error.message}`);
					return throwError(error);
				}
			}),
		);
	}

	private handleServerErrors(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
		let retryDelay = 2_000;
		let retries = 1;

		return next.handle(req).pipe(
			retryWhen((errors) =>
				errors.pipe(
					mergeMap((err) =>
						iif(
							() => retries === environment.api.errorHandling.maxNumOfRetries,
							throwError(err).pipe(
								tap(() =>
									this.handleErrorLogging(
										`Finish retries, HTTP server side error - error code: ${err.status}, error message: ${err.message}`,
									),
								),
							),
							of(err).pipe(
								tap(() => this.logger.error(this.loggerName, `retry number ${retries} for http call: ${err.url}`)),
								tap(() => retries++),
								delay((retryDelay *= 2)),
							),
						),
					),
				),
			),
		);
	}

	// WAF / IP rate limiting (429): retry the request a bounded number of times, honoring `Retry-After`
	// (else exponential backoff + jitter). If still throttled, re-throw for component-level handling
	// (e.g. the bulk-actions "failed, you can retry" toast) WITHOUT the generic error snackbar.
	private handleRateLimitError(error: HttpErrorResponse, req: HttpRequest<any>, next: HttpHandler): Observable<any> {
		return next.handle(req).pipe(
			// `retry({ count, delay })` (not the deprecated `retryWhen`) — the framework passes the correct
			// 1-based `retryCount`, so every slot (incl. the first) is delayed by the right back-off.
			retry({
				count: MAX_RATE_LIMIT_RETRIES,
				delay: (err: HttpErrorResponse, retryCount: number) => {
					if (err?.status !== HTTP_TOO_MANY_REQUESTS) {
						return throwError(() => err);
					}
					this.logger.error(this.loggerName, `429 rate-limited, retry ${retryCount} for ${err.url}`);
					return timer(this.rateLimitBackoffMs(err, retryCount));
				},
			}),
			catchError((err: HttpErrorResponse) => {
				// Exhausted 429 retries → re-throw silently (bulk aggregates; components handle their own).
				if (err?.status === HTTP_TOO_MANY_REQUESTS) {
					this.logger.error(this.loggerName, `Rate limit retries exhausted for ${err.url}`);
					return throwError(() => err);
				}
				// A non-429 surfaced during the retry loop — route it back through the normal handlers so
				// 401/session, 409 concurrent, 5xx retry, etc. are not bypassed.
				if (err?.status >= 400 && err.status < 500) {
					return this.handleClientErrors(err, req);
				}
				if (err?.status >= 500 || err?.status === 0) {
					return this.handleServerErrors(err, req, next);
				}
				return throwError(() => err);
			}),
		);
	}

	// Honor the server's `Retry-After` (seconds) when present; otherwise exponential backoff + jitter, capped.
	private rateLimitBackoffMs(error: HttpErrorResponse, retryCount: number): number {
		const retryAfter = Number(error?.headers?.get('Retry-After'));
		if (retryAfter > 0) {
			return retryAfter * 1_000;
		}
		const backoff = Math.min(1_000 * 2 ** (retryCount - 1), RATE_LIMIT_MAX_BACKOFF_MS);
		return backoff + Math.floor(Math.random() * 250);
	}

	private handleClientErrors(error: HttpErrorResponse, req: HttpRequest<any>): Observable<any> {
		if (!this.authService.isAuthenticated()) {
			this.logger.info(this.loggerName, `User not authenticated`);
			return throwError(error);
		}

		//440 - session expired
		//401 - not authenticated
		//SKIP_GLOBAL_ERROR_SNACKBAR - caller aggregates its own errors (e.g. bulk fan-out) → re-throw silently
		//excluded calls - re-thrown for component-level handling
		//409 - concurrent request
		//403 - no permission
		if (error.status === 440 || this.sessionExpired) {
			return this.handleSessionExpiredError();
		} else if (error.status === 401) {
			return this.handleAuthError(error);
		} else if (req?.context?.get(SKIP_GLOBAL_ERROR_SNACKBAR)) {
			return throwError(error);
		} else if (this.exludedHttpCall(error.url, req?.method)) {
			return throwError(error);
		} else if (error.status === 409) {
			return this.handleConcurrentError(error);
		} else if (error.status === 403) {
			return this.handlePermissionError(error);
		} else {
			this.handleErrorLogging(`HTTP client side error - error code: ${error.status}, error message: ${error.message}`);
			return throwError(error);
		}
	}

	private logoutUser(): Observable<any> {
		const userId = this.authService.getLoggedInUserId();

		if (userId) {
			LocalStorageHelper.removeUserIdAndConnectedCompany(userId);
		}
		return this.authService.logout();
	}

	private handleAuthError(error: HttpErrorResponse): Observable<any> {
		return this.logoutUser().pipe(
			first(),
			tap(() => this.handleErrorSnackbar()),
			tap(() =>
				this.profileQuery.getValue()?.identityProvider?.toLowerCase() !== AuthProviders.auth0
					? this.router.navigate([RoutesManager.login])
					: null,
			),
			switchMap(() => throwError(error)),
		);
	}

	// 403 - the user's role lacks permission for the call. Show a dedicated snackbar (not the generic
	// error) and swallow the error (like 409) — re-throwing makes callers' own catchError show a second,
	// generic snackbar for the same failure.
	// The BE may return a machine reason in the body (e.g. { reason: 'tenant_not_onboarded' }): the CMS keys
	// in `common` are camelCase (e.g. `common.tenantNotOnboarded`), so the snake_case reason is converted
	// before lookup. Fallbacks: raw reason, then the generic permission copy.
	private handlePermissionError(error: HttpErrorResponse): Observable<void> {
		this.logger.error(this.loggerName, `HTTP permission error (403) - url: ${error.url}, error message: ${error.message}`);
		const reason = error.error?.reason;
		const message = (reason ? this.contentPipe.transform(`common.${this.snakeToCamelCase(reason)}`) || reason : null)
			|| this.contentPipe.transform('common.permissionError');
		this.snackbarService.showTimed(MineSnackbarType.Error, message);
		this.hotjar.event('error-snackbar');
		return EMPTY;
	}

	private snakeToCamelCase(value: string): string {
		return value.replace(/_+(\w)/g, (_, char: string) => char.toUpperCase());
	}

	private handleConcurrentError(error: HttpErrorResponse): Observable<void> {
		this.logger.error(this.loggerName, error.message);
		this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform('common.concurrentError'));
		this.hotjar.event('error-snackbar');
		return EMPTY;
	}

	private handleSessionExpiredError(): Observable<any> {
		// when already expired - we don't want to open the dialog again
		if (this.sessionExpired) {
			return EMPTY;
		}

		this.sessionExpired = true;
		this.sessionExpiredDialogRef = this.dialogsManagerService.openDialogCommon(
			SessionExpiredDialogComponent,
			null,
			'450px',
			false,
		);
		return this.sessionExpiredDialogRef?.afterClosed$.pipe(
			switchMap(() => this.logoutUser()),
			tap(() => this.router.navigate([RoutesManager.login])),
			tap(() => this.resetSessionExpiredState()),
			switchMap(() => EMPTY),
		);
	}

	private resetSessionExpiredState(): void {
		this.sessionExpiredDialogRef = undefined;
		this.sessionExpired = false;
	}

	handleErrorSnackbar(): void {
		this.snackbarService.showTimed(MineSnackbarType.Error, this.contentPipe.transform('common.defaultError'));
		this.hotjar.event('error-snackbar');
	}

	private handleErrorLogging(logMsg: string): void {
		this.logger.error(this.loggerName, logMsg);
		this.handleErrorSnackbar();
	}

	private exludedHttpCall(url: string, method?: string): boolean {
		return EXCLUDED_HTTP_CALLS.some((entry) => {
			if (typeof entry === 'string') {
				return url.includes(entry);
			}
			if ('paths' in entry) {
				return entry.paths.every(p => url.includes(p)) && entry.method.includes(method);
			}
			return url.includes(entry.path) && entry.method.includes(method);
		});
	}
}
